import {Role, type RoleData} from "@/types/roles/role";
import {LoginAsRole} from "@/types/roles/login-as-roles";
import {SalePersonPurchaser, type SalePersonPurchaserData} from "@/types/erp/SalePersonPurchaser";
import {OptionValue, type OptionValueData} from "@/types/site_admin/option-values";
import {Company, type CompanyData} from "@/types/company/company";
import {KeyAccountManager, type KeyAccountManagerData} from "@/types/erp/KeyAccountManager";
import {Department, type DepartmentData} from "@/types/site_configuration/Department";
import {UserSession, type UserSessionData} from "@/types/users/UserSessions.ts";


export class User {
    public id: number;
    public first_name: string;
    public last_name: string;
    public phone_number: string;
    public email: string;
    public job_title: string;
    public department: string;
    public image_data: string | null;
    public blocked: number;
    public blocking_reason: string;
    public language: string;
    public sales_person_purchaser_id: number | null;
    public tms_owner_id: number | null;
    public department_id: number | null;
    public key_account_id: number | null;
    public is_contract_manager: boolean;
    public is_active: boolean;
    public strict_invoice_filter: boolean;
    public raws_per_page_chart_dashboard: number;
    public last_access: Date | null;
    public isDomesticResponsible: boolean;
    public isInternationalResponsible: boolean;
    public isStrongoResponsible: boolean;
    public image_contents: string;
    public image_type: string;
    public total_access: number;
    public daily_access: number;
    public weekly_access: number;
    public monthly_access: number;
    public average_session: number;
    public uses_mcp: boolean;
    public lastReleaseRead: any | null;
    public contractManagers: Company[];
    public roles: Role[];
    public loginAsRoles: LoginAsRole[];
    public salesManager: SalePersonPurchaser | null;
    public keyAccountManager: KeyAccountManager | null;
    public tmsOwner: OptionValue | null;
    public oneclickDepartment: Department | null;
    public userSessions: User[] | undefined;

    constructor(data: UserData | undefined = undefined) {
        if (data === undefined) {
            this.id = 0;
            this.first_name = "";
            this.last_name = "";
            this.phone_number = "";
            this.email = "";
            this.job_title = "";
            this.department = "";
            this.image_data = null;
            this.blocked = 0;
            this.blocking_reason = "";
            this.language = "";
            this.sales_person_purchaser_id = null;
            this.tms_owner_id = null;
            this.department_id = null;
            this.key_account_id = null;
            this.is_contract_manager = false;
            this.is_active = true;
            this.strict_invoice_filter = false;
            this.image_contents = '';
            this.image_type = '';
            this.total_access = 0;
            this.daily_access = 0;
            this.weekly_access = 0;
            this.monthly_access = 0;
            this.average_session = 0;
            this.uses_mcp = false;
            this.last_access = null;
            this.salesManager = null;
            this.keyAccountManager = null;
            this.tmsOwner = null;
            this.oneclickDepartment = null;
            this.raws_per_page_chart_dashboard = 3;
            this.isDomesticResponsible = false;
            this.isInternationalResponsible = false;
            this.isStrongoResponsible = false;
            this.lastReleaseRead = null;
            this.contractManagers = [];
            this.roles = [];
            this.loginAsRoles = [];
            this.userSessions = [];
        } else {
            this.id = data.id;
            this.first_name = data.first_name;
            this.last_name = data.last_name;
            this.phone_number = data.phone_number;
            this.email = data.email;
            this.job_title = data.job_title;
            this.department = data.department;
            this.image_data = data.image_data;
            this.blocked = data.blocked;
            this.blocking_reason = data.blocking_reason;
            this.language = data.language;
            this.sales_person_purchaser_id = data.sales_person_purchaser_id;
            this.tms_owner_id = data.tms_owner_id;
            this.department_id = data.department_id;
            this.key_account_id = data.key_account_id;
            this.is_contract_manager = data.is_contract_manager;
            this.is_active = data.is_active;
            this.strict_invoice_filter = data.strict_invoice_filter;
            this.raws_per_page_chart_dashboard = data.raws_per_page_chart_dashboard;
            this.isDomesticResponsible = data.isDomesticResponsible;
            this.isInternationalResponsible = data.isInternationalResponsible;
            this.isStrongoResponsible = data.isStrongoResponsible;
            this.image_contents = data.image_contents;
            this.image_type = data.image_type;
            this.total_access = data.total_access;
            this.daily_access = data.daily_access;
            this.weekly_access = data.weekly_access;
            this.monthly_access = data.monthly_access;
            this.average_session = data.average_session;
            this.uses_mcp = data.uses_mcp;
            this.roles = [];
            if (data.roles !== undefined) {
                data.roles.forEach(role => this.roles.push((new Role(role))));
            }
            this.loginAsRoles = [];
            if (data.loginAsRoles !== undefined) {
                data.loginAsRoles.forEach(login_as_role => this.loginAsRoles.push((new LoginAsRole(login_as_role))));
            }
            this.salesManager = null;
            if (data.salesManager !== null && data.salesManager !== undefined) {
                this.salesManager = new SalePersonPurchaser(data.salesManager);
            }
            this.keyAccountManager = null;
            if (data.keyAccountManager !== null && data.keyAccountManager !== undefined) {
                this.keyAccountManager = new KeyAccountManager(data.keyAccountManager);
            }
            this.tmsOwner = null;
            if (data.tmsOwner !== null && data.tmsOwner !== undefined) {
                this.tmsOwner = new OptionValue(data.tmsOwner);
            }
            this.oneclickDepartment = null;
            if (data.oneclickDepartment !== null && data.oneclickDepartment !== undefined) {
                this.oneclickDepartment = new Department(data.oneclickDepartment);
            }
            this.lastReleaseRead = null;
            if (data.lastReleaseRead !== null) {
                this.lastReleaseRead = data.lastReleaseRead;
            }
            this.contractManagers = [];
            if (data.contractManagers !== undefined) {
                data.contractManagers.forEach(contractManager => this.contractManagers.push((new Company(contractManager))));
            }
            this.last_access = null;
            if (data.last_access !== null) {
                this.last_access = new Date(data.last_access);
            }
            this.userSessions = [];
            if (data.userSessions !== undefined) {
                data.userSessions.forEach(userSession => this.userSessions.push((new UserSession(userSession))));
            }
        }
    }

    getInitials(glue: boolean | undefined): string | Array<string> {
        if (typeof glue === "undefined") {
            glue = true;
        }

        let full_name = this.getFullName();

        const initials = full_name.replace(/[^a-zA-Z- ]/g, "").match(/\b\w/g);

        if (initials === null) {
            if (glue) {
                return "";
            } else {
                return [];
            }
        }

        if (glue) {
            return initials.join('');
        }

        return initials;
    };

    getFullName(): string {
        return `${this.first_name} ${this.last_name}`;
    }

    getProfilePicture(): string | null {
        return `data:${this.image_type};base64,${this.image_contents}`;
    }

    nameContains(search: string) {
        return this.getFullName().trim().toLowerCase().indexOf(search.trim().toLowerCase()) >= 0;
    }

    emailContains(search: string, case_insensitive: boolean = true) {
        return this.email.trim().toLowerCase().indexOf(search.trim().toLowerCase()) >= 0;
    }

    roleNameContains(search: string, case_insensitive: boolean = true) {
        const searchTerm = search.trim().toLowerCase();
        return this.roles.some(el => el.name.trim().toLowerCase().indexOf(searchTerm) >= 0);
    }

    loginAsRoleNameContains(search: string, case_insensitive: boolean = true) {
        const searchTerm = search.trim().toLowerCase();
        return this.loginAsRoles.some(el => el.name.trim().toLowerCase().indexOf(searchTerm) >= 0);
    }

    public serializeData(without_relations: boolean | Array<string> = false, with_relations: boolean | Array<string> = false, ids_only: boolean = false): UserData {

        const serialized_data = {

            id: this.id,
            first_name: this.first_name,
            last_name: this.last_name,
            phone_number: this.phone_number,
            email: this.email,
            job_title: this.job_title,
            department: this.department,
            image_data: this.image_data,
            blocked: this.blocked,
            blocking_reason: this.blocking_reason,
            language: this.language,
            sales_person_purchaser_id: this.sales_person_purchaser_id,
            tms_owner_id: this.tms_owner_id,
            department_id: this.department_id,
            key_account_id: this.key_account_id,
            is_contract_manager: this.is_contract_manager,
            is_active: this.is_active,
            strict_invoice_filter: this.strict_invoice_filter,
            raws_per_page_chart_dashboard: this.raws_per_page_chart_dashboard,
            total_access: this.total_access,
            daily_access: this.daily_access,
            weekly_access: this.weekly_access,
            monthly_access: this.monthly_access,
            average_session: this.average_session,
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('roles')
            ) ||
            (
                Array.isArray(with_relations) &&
                with_relations.includes('roles')
            )

        ) {
            serialized_data.roles = [];
            this.roles.forEach(el => {
                if (ids_only === true) {
                    serialized_data.roles.push({id: el.id})
                } else {
                    if (typeof el.serializeData === "function") {
                        serialized_data.roles.push(el.serializeData())
                    } else {
                        serialized_data.roles.push(el)
                    }
                }
            })
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('loginAsRoles')
            ) ||
            (
                Array.isArray(with_relations) &&
                with_relations.includes('loginAsRoles')
            )

        ) {
            serialized_data.loginAsRoles = [];
            this.loginAsRoles.forEach(el => {
                if (ids_only === true) {
                    serialized_data.loginAsRoles.push({id: el.id})
                } else {
                    if (typeof el.serializeData === "function") {
                        serialized_data.loginAsRoles.push(el.serializeData())
                    } else {
                        serialized_data.loginAsRoles.push(el)
                    }
                }
            })
        }

        if (without_relations === false ||
            (
                Array.isArray(without_relations) &&
                !without_relations.includes('userSessions')
            ) ||
            (
                Array.isArray(with_relations) &&
                with_relations.includes('userSessions')
            )

        ) {
            serialized_data.userSessions = [];
            this.userSessions.forEach(el => {
                if(ids_only === true)
                {
                    serialized_data.userSessions.push({id: el.id})
                }
                else
                {
                    if(typeof el.serializeData === "function")
                    {
                        serialized_data.userSessions.push(el.serializeData())
                    }
                    else
                    {
                        serialized_data.userSessions.push(el)
                    }
                }
            })
        }

        return serialized_data;
    }
}

export type UserData = {
    id: number;
    first_name: string;
    last_name: string;
    phone_number: string;
    email: string;
    job_title: string;
    department: string;
    image_data: string | null;
    roles: RoleData[] | undefined;
    loginAsRoles: LoginAsRole[] | undefined;
    blocked: number;
    blocking_reason: string;
    language: string;
    accountPermissions: Array<string> | undefined;
    sales_person_purchaser_id: number | null;
    tms_owner_id: number | null;
    department_id: number | null;
    key_account_id: number | null;
    is_contract_manager: boolean;
    is_active: boolean;
    strict_invoice_filter: boolean;
    raws_per_page_chart_dashboard: number;
    isDomesticResponsible: boolean;
    isInternationalResponsible: boolean;
    isStrongoResponsible: boolean;
    image_contents: string;
    image_type: string;
    total_access: number;
    daily_access: number;
    weekly_access: number;
    monthly_access: number;
    average_session: number;
    uses_mcp: boolean;
    last_access: Date | null;
    salesManager: SalePersonPurchaserData | null;
    keyAccountManager: KeyAccountManagerData | null;
    tmsOwner: OptionValueData | null;
    oneclickDepartment: DepartmentData | null;
    lastReleaseRead: any | null;
    contractManagers: CompanyData[] | undefined;
    userSessions: UserSessionData[] | undefined;
}

